import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { IDynamicElement, IProductComparer, IProductInformation } from '../_shared/models';
import { ProductLayout } from '../_shared/types';
import { ButtonComponent } from '../button/button.component';
import { ProductInformationComponent } from '../product-information/product-information.component';

@Component({
    selector: 'sirius-product-comparer',
    imports: [ProductInformationComponent, ButtonComponent, TranslateModule],
    templateUrl: './product-comparer.component.html',
    styleUrl: './product-comparer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'role': 'group',
        'aria-label': 'Product comparison',
        '[style.--columns]': 'resolvedItems().length',
    },
})
export class ProductComparerComponent implements IDynamicElement<IProductComparer> {
    private readonly _destroyRef = inject(DestroyRef);

    readonly config = input<IProductComparer | undefined>(undefined);
    readonly layout = input<ProductLayout>('product');
    readonly items = input<IProductInformation[]>([]);

    // Resolved values fold the dynamic-element `config` over the scalar inputs:
    // a present `config` wins, otherwise fall through to the direct bindings.
    // Consumers (template + host) read these, not the raw inputs.
    readonly resolvedLayout = computed<ProductLayout>(() => this.config()?.layout ?? this.layout());
    readonly resolvedItems = computed<IProductInformation[]>(() => this.config()?.items ?? this.items());

    readonly canScrollPrev = signal(false);
    readonly canScrollNext = signal(false);
    readonly isScrollable = signal(false);
    readonly pages = signal<number[]>([]);
    readonly activePage = signal(0);

    private readonly _scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');

    constructor() {
        afterNextRender(() => {
            const el = this._scrollContainer()?.nativeElement;
            if (!el) return;

            this.updateScrollState();

            const observer = new ResizeObserver(() => this.updateScrollState());
            observer.observe(el);
            this._destroyRef.onDestroy(() => observer.disconnect());
        });
    }

    onScroll(): void {
        this.updateScrollState();
    }

    scrollPrev(): void {
        const el = this._scrollContainer()?.nativeElement;
        if (!el) return;
        el.scrollBy({ left: -this.getItemWidth(), behavior: 'smooth' });
    }

    scrollNext(): void {
        const el = this._scrollContainer()?.nativeElement;
        if (!el) return;
        el.scrollBy({ left: this.getItemWidth(), behavior: 'smooth' });
    }

    scrollToPage(page: number): void {
        const el = this._scrollContainer()?.nativeElement;
        if (!el) return;
        el.scrollTo({ left: page * this.getItemWidth(), behavior: 'smooth' });
    }

    private updateScrollState(): void {
        const el = this._scrollContainer()?.nativeElement;
        if (!el) return;

        const scrollable = el.scrollWidth > el.clientWidth + 1;
        this.isScrollable.set(scrollable);
        this.canScrollPrev.set(el.scrollLeft > 1);
        this.canScrollNext.set(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);

        if (scrollable) {
            const itemWidth = this.getItemWidth();
            const count = Math.round((el.scrollWidth - el.clientWidth) / itemWidth) + 1;
            if (this.pages().length !== count) {
                this.pages.set(Array.from({ length: count }, (_, i) => i));
            }
            this.activePage.set(Math.round(el.scrollLeft / itemWidth));
        } else {
            this.pages.set([]);
            this.activePage.set(0);
        }
    }

    private getItemWidth(): number {
        const el = this._scrollContainer()?.nativeElement;
        const count = this.resolvedItems().length;
        return el && count > 0 ? el.scrollWidth / count : 0;
    }
}
